/**
 * The differential's comparison (2.G deliverable 23; amendments 2.1 section 4, "Tests"): a mapped
 * stat block against its SRD reference on `DIFFERENTIAL_FIELDS`, the fields the "Link to the SRD
 * entry" default rests on. Feature names compare as a multiset, case and punctuation folded and
 * a trailing parenthetical (a usage such as "(3/Day)" or a cost) left out, over the traits,
 * actions, bonus actions, reactions, legendary options, lair actions and spellcasting blocks.
 */
import { ABILITIES } from '../../schema/common.js';
import { normalizeName } from '../crosswalk/generate.js';
export const DIFFERENTIAL_FIELDS = [
    'armorClass',
    'hitPoints.average',
    'hitPoints.formula',
    ...ABILITIES.map((a) => `abilities.${a}`),
    'challenge.rating',
    'speed.walk',
    'speed.fly',
    'speed.swim',
    'speed.climb',
    'speed.burrow',
    'savingThrows',
    'skills',
    'features',
];
/** A feature's name for the comparison: folded, without a trailing parenthetical. */
export function featureKey(name) {
    return normalizeName(name.replace(/(?:\s*\([^()]{0,80}\))+\s*$/, ''));
}
export function featureNames(block) {
    return [
        ...block.traits,
        ...block.actions,
        ...block.bonusActions,
        ...block.reactions,
        ...(block.legendary?.options ?? []),
        ...(block.lair?.actions ?? []),
        ...block.spellcasting,
    ]
        .map((f) => featureKey(f.name))
        .sort();
}
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const sortedPairs = (list, key) => list.map((x) => `${key(x)}:${x.bonus}`).sort();
function value(block, field) {
    switch (field) {
        case 'armorClass':
            return block.armorClass[0]?.value ?? null;
        case 'hitPoints.average':
            return block.hitPoints.average;
        case 'hitPoints.formula':
            return block.hitPoints.formula;
        case 'challenge.rating':
            return block.challenge.rating;
        case 'savingThrows':
            return sortedPairs(block.savingThrows, (s) => s.ability);
        case 'skills':
            return sortedPairs(block.skills, (s) => s.skill);
        case 'features':
            return featureNames(block);
        default: {
            const [group, key] = field.split('.');
            return block[group][key] ?? null;
        }
    }
}
export function compareToReference(mapped, reference) {
    const differences = DIFFERENTIAL_FIELDS.filter((f) => !same(value(mapped, f), value(reference, f)));
    return { equal: differences.length === 0, differences };
}
//# sourceMappingURL=compare.js.map